import { getGuestsByIds } from "@/lib/api/handlers/GuestHandling";
import { AcceptanceState } from "@/types/enums/AcceptanceState";

/**
 * What did the server actually store for this guest's acceptance?
 *
 * The re-decision surfaces used to PREDICT it: "waitlist" was painted as
 * `SYSTEM`, "normal" as `ACCEPTED`. Only the second is deterministic. The
 * waitlist reroute (`GuestService.java:4012-4023`) fires under
 * `needsCapGate && wouldBlock`, so the request comes back `ACCEPTED` whenever
 *
 *   • the guest already counted against the contingent, or
 *   • capacity freed between the dialog rendering and the click — which is the
 *     ORDINARY case, not an exotic one: the dialog exists BECAUSE capacity was
 *     hit, the admin reads three options and clicks seconds later, and one
 *     decline elsewhere in that window is enough.
 *
 * A prediction that is wrong paints a waitlist badge on an accepted guest, and
 * `RegistrantsProvider` states the invariant it violates 130 lines above the
 * place it violated it: "Waitlist membership is server-owned (forceWaitlist /
 * the promoter CAS)."
 *
 * So ask instead of guessing. One read, only on the branch that is actually
 * ambiguous. `refetchType: "none"` on the cache invalidation means nothing
 * else re-reads this row on its own, so dropping the optimistic paint without
 * this would leave the PRE-write value on screen indefinitely — worse than the
 * wrong guess, not better.
 *
 * Returns `null` when the read fails or answers nothing. Callers keep whatever
 * they had rather than inventing a value: a stale badge beats a fabricated one,
 * and the reconcile wave is still running underneath.
 */
export interface StoredVerdict {
  /** The organiser-facing verdict — drives the acceptance chip. */
  acceptanceState: AcceptanceState;
  /**
   * The guest's own column. Applied as well as read, because the reroute moves
   * BOTH: an earlier version asked for `state` and then dropped it, so the
   * badge updated and the status column kept the pre-write value — two columns
   * describing one fact and disagreeing until something else refetched.
   */
  state: string | null;
}

export async function readBackGuestVerdict(
  eventId: string,
  guestId: string,
): Promise<StoredVerdict | null> {
  try {
    const res = await getGuestsByIds(
      eventId,
      [guestId],
      "",
      [],
      "guestId,acceptanceState,state",
      0,
      1,
    );
    // `getGuestsByIds` RETURNS a ServerError sentinel rather than throwing, so
    // the array check is the sentinel check.
    //
    // Redundant TODAY — without it `res.find` throws on the sentinel and the
    // catch below produces the same null, so no mutation of this line is
    // observable. Kept because it states the intent at the place the sentinel
    // arrives, rather than leaving a documented API contract to be handled by
    // accident three lines down.
    if (!Array.isArray(res)) return null;
    const row = res.find((g) => g.guestId === guestId);
    const value = row?.acceptanceState;
    if (typeof value !== "string") return null;
    const rawState = (row as unknown as { state?: unknown } | undefined)?.state;
    return {
      acceptanceState: value as AcceptanceState,
      state: typeof rawState === "string" ? rawState : null,
    };
  } catch {
    return null;
  }
}
